import type { TFunction } from 'i18next'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'

import { useAdminQuery } from '../api'
import { AdminHeader } from '../AdminLayout'
import { useAdminContext } from '../session'
import type { I18n, QrFamily, QrTarget } from '../types'

// L'ordre des rubriques à l'écran : du mur de la salle aux tables, en
// passant par l'équipe et les liens secrets.
const FAMILIES: QrFamily[] = ['room', 'public', 'staff', 'admin', 'jury', 'screen', 'tables']

/**
 * Tous les liens de la soirée, et le QR qui mène à chacun.
 *
 * Les rubriques ne servent pas les mêmes gens ni le même geste :
 *
 * * la **salle** — le vote et la commande générique, qu'on affiche au mur,
 *   au bar, à l'entrée ;
 * * le **site public**, qu'on partage sur WhatsApp ou une affiche ;
 * * les **écrans de l'équipe** — la porte, les serveurs, la cuisine, le bar,
 *   la caisse, la régie : on tend le QR au renfort d'un soir, il tombe sur son
 *   écran sans taper d'adresse ;
 * * le **back-office** ;
 * * les **jurés** et l'**écran de la salle**, des liens secrets que le
 *   serveur ne sert qu'à qui en a le droit ;
 * * un QR **par table**, qui pré-remplit son numéro.
 *
 * L'écran est conçu pour finir sur du papier. La sélection sert à n'imprimer
 * que ce dont on a besoin — réimprimer la table 12 dont le QR a pris une
 * goutte de sauce ne doit pas coûter trente feuilles.
 */
export function AdminQrCodesPage() {
  const { t } = useTranslation()
  const { eventId } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.qrcodes')
  }, [t])

  const query = useAdminQuery<QrTarget[]>(
    ['admin-qr-codes', eventId],
    `/admin/events/${eventId}/qr-codes`,
    eventId,
  )
  const targets = query.data ?? []

  // Rien de coché = tout s'imprime, SAUF les liens confidentiels. Préparer la
  // salle entière est le cas courant et ne doit demander aucun geste ; mais un
  // lien de juré ou la clé de l'écran imprimés par mégarde finissent sur une
  // table, à la vue de tous. Ceux-là ne partent sur papier que cochés.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const toPrint =
    selected.size === 0
      ? targets.filter((one) => !one.confidential)
      : targets.filter((one) => selected.has(one.key))
  const hasConfidential = targets.some((one) => one.confidential)

  // Le lien copié, pour le dire à côté du bouton : sans retour, on recolle
  // trois fois en se demandant si ça a marché.
  const [copied, setCopied] = useState<string | null>(null)

  function toggle(key: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (!next.delete(key)) next.add(key)
      return next
    })
  }

  function toggleFamily(rows: QrTarget[]) {
    setSelected((current) => {
      const next = new Set(current)
      const all = rows.every((row) => next.has(row.key))
      for (const row of rows) {
        if (all) next.delete(row.key)
        else next.add(row.key)
      }
      return next
    })
  }

  function copy(target: QrTarget) {
    // Le presse-papiers exige une page sûre (https) ; à défaut, l'adresse
    // reste affichée en toutes lettres et se sélectionne à la main.
    void navigator.clipboard
      ?.writeText(target.url)
      .then(() => {
        setCopied(target.key)
        window.setTimeout(() => setCopied((key) => (key === target.key ? null : key)), 2000)
      })
      .catch(() => setCopied(null))
  }

  const groups = FAMILIES.map((family) => ({
    family,
    rows: targets.filter((one) => one.family === family),
  })).filter((group) => group.rows.length > 0)

  return (
    <>
      <AdminHeader
        title={t('admin.qr.title')}
        description={t('admin.qr.intro')}
        action={
          <div className="flex flex-wrap gap-2 print:hidden">
            {selected.size > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                {t('admin.qr.clearSelection')}
              </Button>
            ) : null}
            <Button size="sm" disabled={toPrint.length === 0} onClick={() => window.print()}>
              {t('admin.qr.print', { count: toPrint.length })}
            </Button>
          </div>
        }
      />

      {selected.size === 0 && hasConfidential ? (
        <p className="mb-4 text-caption text-content-muted print:hidden">
          {t('admin.qr.confidentialNotPrinted')}
        </p>
      ) : null}

      {query.isPending ? <Skeleton className="h-64 w-full" /> : null}

      {query.isError ? (
        <p className="text-sm text-state-danger">{query.error.message}</p>
      ) : null}

      {/* L'écran : une rubrique par famille, cases à cocher, liens copiables,
          aperçu du QR. `print:hidden` la retire du papier — la feuille a sa
          propre mise en page, sans boutons ni cases à cocher. */}
      <div className="space-y-8 print:hidden">
        {groups.map(({ family, rows }) => (
          <section key={family} aria-labelledby={`qr-family-${family}`}>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <div className="min-w-0">
                <h2
                  id={`qr-family-${family}`}
                  className="font-display text-heading-md text-content-primary"
                >
                  {t(`admin.qr.family.${family}.title`)}
                </h2>
                <p className="mt-1 text-sm text-content-secondary">
                  {t(`admin.qr.family.${family}.hint`)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => toggleFamily(rows)}
                className="text-caption text-champagne-300 underline"
              >
                {rows.every((row) => selected.has(row.key))
                  ? t('admin.qr.unselectFamily')
                  : t('admin.qr.selectFamily')}
              </button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {rows.map((target) => (
                <QrCard
                  key={target.key}
                  target={target}
                  checked={selected.has(target.key)}
                  copied={copied === target.key}
                  onToggle={() => toggle(target.key)}
                  onCopy={() => copy(target)}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Pas de message « rien à imprimer » : le serveur renvoie toujours au
          moins les QR de vote et de commande sur place. */}

      {/* La feuille. Invisible à l'écran, elle est la seule chose imprimée :
          gros carré, titre lisible de loin, à qui il s'adresse, et l'adresse
          en toutes lettres pour qui préfère la taper. Une cible par page évite
          qu'un QR se retrouve coupé par une césure de page. */}
      <div className="hidden print:block">
        {toPrint.map((target) => (
          <section
            key={target.key}
            className={cn(
              'flex h-screen break-after-page flex-col items-center justify-center gap-6 px-8 text-center',
            )}
          >
            <img src={target.svg} alt="" className="h-80 w-80" />
            <p className="font-display text-4xl text-black">{targetTitle(t, target)}</p>
            <PrintedSubtitle target={target} />
            <p className="max-w-xl text-lg text-black">{targetWho(t, target)}</p>
            <p className="max-w-xl break-all font-mono text-sm text-black">{target.url}</p>
          </section>
        ))}
      </div>
    </>
  )
}

function QrCard({
  target,
  checked,
  copied,
  onToggle,
  onCopy,
}: {
  target: QrTarget
  checked: boolean
  copied: boolean
  onToggle: () => void
  onCopy: () => void
}) {
  const { t } = useTranslation()
  const label = useI18nLabel()
  const zone = target.zone_i18n ? label(target.zone_i18n) : target.zone

  return (
    <Card className="flex min-w-0 gap-3 sm:gap-4">
      <div className="shrink-0 self-start rounded-md bg-white p-2">
        <img src={target.svg} alt="" className="h-20 w-20 sm:h-24 sm:w-24" width={96} height={96} />
      </div>

      <div className="min-w-0 flex-1">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            className="mt-1 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
          />
          <span className="min-w-0">
            <span className="block break-words font-display text-heading-sm text-content-primary">
              {targetTitle(t, target)}
            </span>
            {zone ? <span className="block text-caption text-content-muted">{zone}</span> : null}
            {target.published === false || target.confidential ? (
              <span className="mt-1 flex flex-wrap gap-1">
                {target.published === false ? (
                  <Badge tone="ember">{t('admin.qr.suspended')}</Badge>
                ) : null}
                {target.confidential ? (
                  <Badge tone="ember">
                    {target.kind === 'jury'
                      ? t('admin.qr.personal')
                      : t('admin.qr.confidential')}
                  </Badge>
                ) : null}
              </span>
            ) : null}
          </span>
        </label>

        <p className="mt-2 text-caption text-content-secondary">{targetWho(t, target)}</p>
        {target.roles.length > 0 ? (
          <p className="mt-1 text-caption text-content-muted">
            {t('admin.qr.roles', {
              roles: target.roles.map((role) => label(role.name_i18n, role.code)).join(', '),
            })}
          </p>
        ) : null}

        <p className="mt-2 break-all font-mono text-caption text-content-muted">{target.url}</p>
        <button
          type="button"
          onClick={onCopy}
          className="mt-2 text-caption text-champagne-300 underline"
        >
          {copied ? t('admin.qr.copied') : t('admin.qr.copyLink')}
        </button>
      </div>
    </Card>
  )
}

/** Sur la feuille, la zone d'une table ou le concours d'un juré, sous le titre. */
function PrintedSubtitle({ target }: { target: QrTarget }) {
  const label = useI18nLabel()
  const zone = target.zone_i18n ? label(target.zone_i18n) : target.zone
  return zone ? <p className="text-xl text-black">{zone}</p> : null
}

/** Un libellé multilingue dans la langue de l'écran, sinon la première saisie. */
function useI18nLabel() {
  const { i18n } = useTranslation()
  return (value: I18n, fallback = '') =>
    value[i18n.language] || value[Object.keys(value)[0] ?? ''] || fallback
}

/**
 * Le titre d'une cible.
 *
 * Seuls le numéro de table et le nom d'un juré sont des données ; le reste est
 * traduit. Écrire « Table A3 » côté serveur reviendrait à figer le français
 * dans une réponse d'API que trois langues doivent afficher.
 */
function targetTitle(t: TFunction, target: QrTarget): string {
  if (target.kind === 'table') return t('admin.qr.table', { number: target.label })
  if (target.kind === 'jury') return t('admin.qr.juror', { name: target.label })
  // `page:candidats` → `admin.qr.page.candidats`, `staff:bar` →
  // `admin.qr.staff.bar.title`. Le serveur donne la clé, le front le mot.
  if (target.kind === 'page') return t(`admin.qr.page.${target.key.slice('page:'.length)}`)
  if (target.kind === 'staff') return t(`admin.qr.staff.${target.key.slice('staff:'.length)}.title`)
  return t(`admin.qr.kind.${target.kind}`)
}

/**
 * À qui s'adresse la cible — la phrase imprimée sous le QR.
 *
 * Un écran d'équipe dit son poste ; le reste dit sa rubrique. Sans cette
 * ligne, une pile de feuilles « Bar », « Caisse », « Service » se distribue
 * au hasard.
 */
function targetWho(t: TFunction, target: QrTarget): string {
  if (target.kind === 'staff') return t(`admin.qr.staff.${target.key.slice('staff:'.length)}.who`)
  if (target.kind === 'vote' || target.kind === 'onsite' || target.kind === 'feedback') {
    return t(`admin.qr.who.${target.kind}`)
  }
  return t(`admin.qr.family.${target.family}.who`)
}
